import React, {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Typography, Breadcrumbs } from "cfa-react-components";
import TopNavBar from "../../TopNavBar/TopNavBar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { BackArrow } from "../../Common";
import "./ExcessiveActivityReport.scss";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import I18n from "../../../../i18n/utils";

import {
  ExcessiveActivity,
  ExcessiveActivityAccount,
  Transaction,
} from "../../../interfaces/risk-analysis/ExcessiveActivity";
import ActionableDialog from "../../Common/Dialog/ActionableDialog";
import { getExcessiveActivity } from "../../../utils/api/ExcessiveActivityApi";
import { Button } from "@material-ui/core";
import { GridReadyEvent } from "ag-grid-community";
import { Col, Row } from "reactstrap";

const ExcessiveActivityReport: React.FC = (): JSX.Element => {
  const gridRef: RefObject<any> = useRef();
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);

  const [isActionableDialogDisabled, setIsActionableDialogDisabled] =
    useState<boolean>(true);
  const [selectedCfaIdsByLocation] = useState<Map<string, string[]>>(
    new Map<string, string[]>()
  );
  const [selectedScanCountsByCfaId] = useState<Map<string, number>>(
    new Map<string, number>()
  );

  const transactionSize = (params: {
    data: { transactions: Transaction[] };
  }) => {
    return params.data.transactions.length;
  };

  const highestRiskCFAId = (params: {
    data?: { excessiveActivityAccounts: ExcessiveActivityAccount[] };
  }) => {
    return params?.data?.excessiveActivityAccounts[0].cfaOneId;
  };

  const checkboxSelection = (params: { colDef: { field: string } }) => {
    return params.colDef.field === "cfaOneId";
  };

  //TODO Structure should change to be an object that can be used to keep location / cfaId and scan count state.
  //TODO See IActionableDialog.tsx
  const onCheckBoxSelected = (
    excessiveActivityAccount: ExcessiveActivityAccount,
    locationId: string
  ) => {
    const cfaId = excessiveActivityAccount.cfaOneId;
    const scanCount = excessiveActivityAccount.transactions.length;
    const cfaIds: string[] | undefined =
      selectedCfaIdsByLocation.get(locationId);
    const index = cfaIds?.indexOf(cfaId);
    if (index !== undefined && index !== -1) {
      cfaIds?.splice(index, 1);
      selectedScanCountsByCfaId.delete(cfaId);
      if (cfaIds?.length === 0) {
        selectedCfaIdsByLocation.delete(locationId);
      }
    } else if (cfaIds === undefined) {
      selectedCfaIdsByLocation.set(locationId, [cfaId]);
      selectedScanCountsByCfaId.set(cfaId, scanCount);
    } else {
      cfaIds.push(cfaId);
    }
    setIsActionableDialogDisabled(selectedCfaIdsByLocation.size == 0);
    return selectedCfaIdsByLocation;
  };

  const [columnDefs] = useState([
    {
      headerName: "Location",
      field: "locationId",
      cellRenderer: "agGroupCellRenderer",
    },
    { field: "mostExcessiveActivityDate", sortable: true },
    { field: "highestRiskCfaId", valueGetter: highestRiskCFAId },
  ]);

  const detailCellRendererParams = useMemo(() => {
    return (params: any) => {
      return {
        detailGridOptions: {
          columnDefs: [
            {
              field: "locationId",
              hide: true,
            },
            {
              field: "cfaOneId",
              cellRenderer: "agGroupCellRenderer",
              cellRendererParams: { checkbox: checkboxSelection },
            },
            { field: "uniquePaymentSourceCount" },
            { field: "Transaction Count", valueGetter: transactionSize },
          ],
          defaultColDef: {
            flex: 1,
          },
          masterDetail: true,
          refreshStrategy: "everything",
          rowSelection: "multiple",
          onRowSelected: (row: any) =>
            onCheckBoxSelected(row.data, params.data.locationId),
          suppressRowSelection: true,
          detailCellRendererParams: {
            detailGridOptions: {
              columnDefs: [
                { field: "transactionId", cellRenderer: "agGroupCellRenderer" },
                { field: "pointOfSaleNumber" },
                { field: "date" },
              ],
              defaultColDef: {
                flex: 1,
              },
              suppressRowSelection: true,
            },
            getDetailRowData: (params: any) => {
              params.successCallback(params.data.transactions);
            },
          },
        },
        getDetailRowData: (params: any) => {
          params.successCallback(params.data.excessiveActivityAccounts);
        },
      };
    };
  }, []);

  const refreshView = useCallback((): void => {
    selectedCfaIdsByLocation.clear();
    setIsActionableDialogDisabled(true);
    gridRef.current.api.setRowData(
      excessiveActivity.current?.excessiveActivityLocations
    );
  }, []);

  const excessiveActivity: MutableRefObject<ExcessiveActivity | undefined> =
    useRef();
  const [isDataLoaded, setIsDateLoaded] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  useEffect(() => {
    if (!isDataLoaded) {
      //TODO Add loading spinner
      getExcessiveActivity(startDate, endDate).then(
        (excessiveActivityData: ExcessiveActivity) => {
          excessiveActivity.current = excessiveActivityData;
          gridRef.current.api.setRowData(
            excessiveActivity.current.excessiveActivityLocations
          );
          //Typescript & Javascript are dumb
          let startDateFormat: string =
            excessiveActivity.current.startDate.toString();
          startDateFormat = startDateFormat.replace("-", "/");
          setStartDate(new Date(startDateFormat));

          let endDateFormat: string =
            excessiveActivity.current.endDate.toString();
          endDateFormat = endDateFormat.replace("-", "/");
          setEndDate(new Date(endDateFormat));
          setIsDateLoaded(true);
        }
      );
    }
  }, [isDataLoaded, startDate, endDate]);

  const handlePickerStartChange = useCallback((date: Date) => {
    setStartDate(date);
  }, []);

  const handlePickerEndChange = useCallback((date: Date) => {
    setEndDate(date);
  }, []);

  // @ts-ignore
  const preventBlurChange = useCallback((e: FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
  }, []);

  const queryDateRange = useCallback(() => {
    setIsDateLoaded(false);
  }, []);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    event.api.sizeColumnsToFit();
    gridRef.current.api.setRowData(
      excessiveActivity.current?.excessiveActivityLocations
    );
  }, []);

  return (
    <Col className={"Sherlock Container"}>
      <Row>
        <div style={{ marginTop: "2rem" }}>
          <Breadcrumbs
            breadcrumbs={[{ label: "Risk Analysis" }, { label: "EAR" }]}
          />
        </div>
      </Row>
      <Row style={{ marginTop: "1rem", display: "flex" }}>
        <Col md={3} style={{ display: "flex" }}>
          <div className={"Header"}>
            <Typography
              variant={"h3"}
              style={{ display: "flex", color: "black", alignSelf: "flex-end" }}
            >
              Excessive Activity Report
            </Typography>
          </div>
        </Col>
      </Row>
      <Row className={"TableContainer"}>
        <Col style={{ display: "flex", flexDirection: "column" }}>
          <>
            <div className="RiskAnalysis container">
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div>
                    <span style={{ marginRight: "1rem" }}>
                      {I18n.t("APP_RISK_ANALYSIS_START_DATE")}
                    </span>
                    <DatePicker
                      wrapperClassName="datePicker"
                      dateFormat={"yyyy-MM-dd"}
                      selected={startDate}
                      onChange={handlePickerStartChange}
                      onChangeRaw={preventBlurChange}
                    />
                  </div>
                  <div style={{ marginTop: ".5em" }}>
                    <span style={{ marginRight: "1.5rem" }}>
                      {I18n.t("APP_RISK_ANALYSIS_END_DATE")}
                    </span>
                    <DatePicker
                      wrapperClassName="datePicker"
                      dateFormat={"yyyy-MM-dd"}
                      selected={endDate}
                      onChange={handlePickerEndChange}
                      onChangeRaw={preventBlurChange}
                    />
                  </div>
                  <div>
                    <Button
                      className={"actionableDialogButton"}
                      style={{ marginTop: "1.5em", float: "right" }}
                      onClick={queryDateRange}
                    >
                      Query Date Range
                    </Button>
                  </div>
                </div>
                <div style={{ alignSelf: "end" }}>
                  <ActionableDialog
                    data={{
                      selectedCfaIdsByLocation,
                      selectedScanCountsByCfaId,
                    }}
                    startDate={excessiveActivity.current?.startDate}
                    endDate={excessiveActivity.current?.endDate}
                    isDisabled={isActionableDialogDisabled}
                    refreshView={refreshView}
                  />
                </div>
              </div>
              <div style={gridStyle} className="ag-theme-alpine">
                <AgGridReact
                  ref={gridRef}
                  // @ts-ignore
                  columnDefs={columnDefs}
                  animateRows={true}
                  rowSelection="multiple"
                  groupDisplayType="singleColumn"
                  suppressRowClickSelection={true}
                  masterDetail={true}
                  detailRowAutoHeight={true}
                  detailCellRendererParams={detailCellRendererParams}
                  onGridReady={onGridReady}
                />
              </div>
            </div>
          </>
        </Col>
      </Row>
    </Col>
  );
};
export default ExcessiveActivityReport;
